import React, { useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Lock, MapPin, MonitorPlay, Check } from "lucide-react";
import { MainLayout } from "@/layouts/MainLayout";
import { useMockQuery } from "@/hooks/useMockQuery";
import { apiClient } from "@/lib/apiClient";
import { TicketCard } from "@/components/tickets/TicketCard";
import { EventStatusBadge } from "@/components/EventStatusBadge";
import { ErrorState } from "@/components/states/ErrorState";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { grantAccess } from "@/lib/access";

const SERVICE_FEE = 1.5;

const COMPARE = [
  { key: "inPerson", label: "In person", Icon: MapPin, rows: {
    access: "At the venue", replay: "—", capacity: "Limited seats", perkKey: "inPerson" } },
  { key: "online", label: "Online", Icon: MonitorPlay, rows: {
    access: "Stream anywhere", replay: "Included", capacity: "Unlimited", perkKey: "online" } },
];

export default function Checkout() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data: event, loading, error, refetch } = useMockQuery(
    () => apiClient.getEvent(eventId),
    [eventId]
  );

  const [selected, setSelected] = useState(params.get("type") === "online" ? "online" : "inPerson");
  const [processing, setProcessing] = useState(false);

  if (loading)
    return (
      <MainLayout>
        <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
          <Skeleton className="mb-8 h-10 w-52 rounded-lg" />
          <div className="grid gap-8 lg:grid-cols-3">
            <Skeleton className="h-96 rounded-2xl lg:col-span-2" />
            <Skeleton className="h-80 rounded-2xl" />
          </div>
        </div>
      </MainLayout>
    );

  if (error || !event || event.type !== "premium")
    return (
      <MainLayout>
        <div className="mx-auto max-w-3xl px-5 py-24">
          <ErrorState
            title="Checkout unavailable"
            description="This event doesn't have paid tickets."
            error={error}
            onRetry={refetch}
          />
        </div>
      </MainLayout>
    );

  const ticket = event.tickets[selected];
  const ticketSoldOut = ticket.soldOut || ticket.available === 0;
  const total = ticketSoldOut ? 0 : ticket.price + SERVICE_FEE;

  const complete = () => {
    setProcessing(true);
    setTimeout(() => {
      grantAccess(event.id, selected);
      toast.success("Purchase complete (demo)", {
        description: "No real payment was taken. Access unlocked.",
      });
      navigate(`/event/${event.id}?unlocked=1`);
    }, 1100);
  };

  return (
    <MainLayout>
      <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
        <Link
          to={`/event/${event.id}`}
          className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to event
        </Link>

        <div className="mb-8 flex flex-wrap items-center gap-3">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Checkout
          </h1>
          <EventStatusBadge status="premium" />
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Left — selection + comparison */}
          <div className="space-y-8 lg:col-span-2">
            <div>
              <h2 className="mb-4 font-heading text-lg font-bold tracking-tight text-foreground">
                Pick your ticket
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                <TicketCard
                  type="inPerson"
                  ticket={event.tickets.inPerson}
                  selected={selected === "inPerson"}
                  onSelect={setSelected}
                  compact
                />
                <TicketCard
                  type="online"
                  ticket={event.tickets.online}
                  selected={selected === "online"}
                  onSelect={setSelected}
                  compact
                />
              </div>
            </div>

            {/* Comparison table */}
            <div className="overflow-hidden rounded-2xl border border-border">
              <div className="grid grid-cols-3 bg-secondary/60 text-sm font-semibold text-foreground">
                <div className="p-4">Compare</div>
                {COMPARE.map((c) => (
                  <div
                    key={c.key}
                    className={`flex items-center gap-2 p-4 ${
                      selected === c.key ? "text-brand" : "text-muted-foreground"
                    }`}
                  >
                    <c.Icon className="h-4 w-4" /> {c.label}
                  </div>
                ))}
              </div>
              {[
                { label: "Access", get: (c) => c.rows.access },
                { label: "Replay", get: (c) => c.rows.replay },
                { label: "Availability", get: (c) => c.rows.capacity },
                {
                  label: "Price",
                  get: (c) => {
                    const t = event.tickets[c.key];
                    return t.soldOut || t.available === 0 ? "Sold out" : `${t.currency}${t.price}`;
                  },
                },
              ].map((row) => (
                <div key={row.label} className="grid grid-cols-3 border-t border-border text-sm">
                  <div className="p-4 font-medium text-muted-foreground">{row.label}</div>
                  {COMPARE.map((c) => (
                    <div
                      key={c.key}
                      className={`p-4 ${
                        selected === c.key ? "bg-brand/5 font-semibold text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {row.get(c)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Right — order summary */}
          <div>
            <div className="sticky top-24 space-y-5 rounded-3xl border border-border bg-card p-6 shadow-sm">
              <h3 className="font-heading text-lg font-bold tracking-tight text-foreground">
                Order summary
              </h3>
              <div className="flex items-center gap-3 rounded-2xl bg-secondary/50 p-3">
                <img src={event.cover} alt="" className="h-14 w-20 rounded-xl object-cover" />
                <div className="min-w-0">
                  <p className="line-clamp-1 text-sm font-semibold text-foreground">{event.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {selected === "inPerson" ? "Attend in person" : "Watch online"}
                  </p>
                </div>
              </div>

              <div className="space-y-2 border-t border-border pt-4 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>Ticket</span>
                  <span>{ticketSoldOut ? "—" : `${ticket.currency}${ticket.price.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Service fee</span>
                  <span>{ticketSoldOut ? "—" : `€${SERVICE_FEE.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between border-t border-border pt-2 font-heading text-base font-extrabold text-foreground">
                  <span>Total</span>
                  <span>{ticketSoldOut ? "Sold out" : `€${total.toFixed(2)}`}</span>
                </div>
              </div>

              <Button
                data-testid="complete-purchase-btn"
                disabled={ticketSoldOut || processing}
                onClick={complete}
                className="w-full rounded-full bg-brand py-6 text-base hover:bg-brand-hover disabled:opacity-60"
              >
                {processing ? (
                  "Processing…"
                ) : ticketSoldOut ? (
                  "Sold out"
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" /> Complete purchase
                  </>
                )}
              </Button>

              <p className="flex items-start gap-2 rounded-xl bg-secondary/60 p-3 text-xs text-muted-foreground">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                Demo checkout — no payment gateway connected. Completing this only unlocks the
                event in this prototype.
              </p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
